import { useCallback, useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, Trash2, RefreshCw, FileText, Archive, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SectionCard, Field, Skeleton, StepProgressList } from './pipeline/ui';

// CA4 — the corpus admin's working surface: datasets -> documents -> upload / parse / delete.
//
// TWO RULES THIS UI EXISTS TO ENFORCE, both learned the expensive way:
//   1. NO BLIND MONEY BUTTONS. Parsing is the billable step and the enrichment presets multiply
//      it (RAPTOR/GraphRAG run an LLM inside RAGFlow that never reports its spend). So the cost
//      estimate is fetched and shown BEFORE upload and BEFORE re-parse, every time.
//   2. UPLOAD AND PARSE ARE SEPARATE ACTIONS. Storing a file is free; indexing it is not. Making
//      the second step explicit means nobody spends credits by dragging a file in.
const PROVENANCE = ['authored', 'external'];

function StatusPill({ status, detail }) {
  // N-87: 'partial' is the coverage guard speaking — measured ingest covered a fraction of the
  // file. It must look like a problem (destructive), never like success, and carry its cause.
  const tone = status === 'indexed' ? 'text-success'
    : status === 'failed' ? 'text-destructive'
    : status === 'partial' ? 'text-destructive'
    : status === 'parsing' ? 'text-warning'
    : 'text-muted-foreground';
  return (
    <span className={`text-xs font-medium ${tone}`} title={detail || undefined}
      data-testid={`doc-status-${status}`}>
      {status}
    </span>
  );
}

export default function CorpusDocumentsCard({ datasets }) {
  const { t } = useTranslation();
  const [dataset, setDataset] = useState('');
  // N-64: an upload must name the OWNING engine. Before this, connection_id was never sent, so
  // every write resolved to the platform corpus — fine while only a superadmin could reach the
  // card, and refused by the 062 guard the moment a user could.
  const connectionOf = useCallback(
    (name) => (datasets || []).find((d) => d.name === name)?.connection_id ?? null,
    [datasets],
  );
  const [docs, setDocs] = useState(null);
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState('standard');
  const [provenance, setProvenance] = useState('authored');
  // N-87: uploads come in batches (the beverage corpus is 17 files); one-at-a-time selection was
  // the single biggest friction in the first live build. Free step, so no per-file confirm.
  const [files, setFiles] = useState([]);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  // N-90: what the batch is doing right now, so a 17-file run is not a frozen page. Counted, not
  // timed - every one of these loops is sequential and knows its own denominator.
  const [job, setJob] = useState(null); // { key, items: [{ id, label, status, note }] }

  // N-90: advance ONE row of the running job. Rows are the unit because a batch of seventeen has
  // seventeen answers, not one - and a file that fails must say so on its own line, next to its
  // own name, instead of disappearing into an average.
  const stepRow = (id, status, note) => setJob((j) => (j
    ? { ...j, items: j.items.map((it) => (it.id === id ? { ...it, status, note: note ?? it.note } : it)) }
    : j));

  useEffect(() => {
    api.corpusAdmin('ingest_presets').then((r) => setPresets(r.presets || [])).catch(() => setPresets([]));
  }, []);

  const loadDocs = useCallback((name) => {
    if (!name) { setDocs([]); return; }
    api.corpusAdmin('list_documents', { dataset_name: name })
      .then((r) => setDocs(r.documents || []))
      .catch((e) => { toast.error(e.message); setDocs([]); });
  }, []);

  useEffect(() => { loadDocs(dataset); }, [dataset, loadDocs]);

  // Quote the parse before anything is spent. Re-quoted whenever the files or preset change, so
  // the number on screen always belongs to the action about to happen. One quote over the summed
  // size: the cost is linear in tokens, and start_parse re-derives per file anyway.
  useEffect(() => {
    if (files.length === 0) { setQuote(null); return; }
    let cancelled = false;
    const size_bytes = files.reduce((s, f) => s + f.size, 0);
    api.corpusAdmin('ingest_cost_preview', { size_bytes, preset })
      .then((r) => { if (!cancelled) setQuote(r); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [files, preset]);

  const toBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

  const upload = async () => {
    if (!dataset || files.length === 0) { toast.error(t('corpusDocs.pickDatasetAndFile')); return; }
    setBusy(true);
    let ok = 0;
    try {
      // Sequential, not parallel: each failure is reported with ITS filename, and one bad file
      // never aborts the rest of the batch.
      setJob({
        key: 'upload',
        items: files.map((f) => ({ id: f.name, label: f.name, status: 'idle', note: t('corpusDocs.step.queued') })),
      });
      for (const f of files) {
        stepRow(f.name, 'active', t('corpusDocs.step.uploading'));
        try {
          const content_base64 = await toBase64(f);
          const send = (overwrite) => api.corpusAdmin('ingest_document', {
            dataset_name: dataset, filename: f.name, content_base64, provenance,
            connection_id: connectionOf(dataset), ...(overwrite ? { overwrite: true } : {}),
          });
          try {
            await send(false);
          } catch (e) {
            // N-89: the server now REFUSES a same-name upload rather than silently replacing an
            // indexed document. Ask, using the server's own message - it names the chunks and
            // tokens at stake - and only then send the deliberate overwrite.
            if (!/already exists/i.test(e.message || '')) throw e;
            if (!window.confirm(`${e.message}

${t('corpusDocs.overwriteConfirm', { filename: f.name })}`)) {
              toast.info(t('corpusDocs.overwriteSkipped', { filename: f.name }));
              stepRow(f.name, 'warn', t('corpusDocs.step.skipped'));
              continue;
            }
            await send(true);
          }
          ok += 1;
          stepRow(f.name, 'done', t('corpusDocs.step.uploaded'));
        } catch (e) {
          toast.error(`${f.name}: ${e.message}`);
          stepRow(f.name, 'error', e.message.slice(0, 60));
        }
      }
      if (ok > 0) toast.success(t('corpusDocs.uploadedMany', { count: ok }));
      setFiles([]);
      loadDocs(dataset);
    } finally { setBusy(false); setJob(null); }
  };

  // N-87: parse everything that is uploaded but not yet indexed, as ONE decision — one summed
  // quote, one confirm, then sequential start_parse. Rule 1 of this card (no blind money buttons)
  // holds: the number shown is the sum of per-document quotes for exactly the parses about to run.
  const parseAll = async () => {
    const pending = (docs || []).filter((d) => !d.retired && !['indexed', 'parsing'].includes(d.status));
    if (pending.length === 0) { toast.info(t('corpusDocs.parseAllNone')); return; }
    setBusy(true);
    try {
      let total = 0;
      setJob({
        key: 'quote',
        items: pending.map((d) => ({ id: d.id, label: d.filename, status: 'idle', note: t('corpusDocs.step.pricing') })),
      });
      for (const d of pending) {
        stepRow(d.id, 'active', t('corpusDocs.step.pricing'));
        try {
          const q = await api.corpusAdmin('ingest_cost_preview', { document_id: d.id, preset });
          total += Number(q.estimated_credits ?? 0);
          stepRow(d.id, 'done', t('corpusDocs.step.priced'));
        } catch {
          /* start_parse re-derives; a failed preview does not block the decision */
          stepRow(d.id, 'warn', t('corpusDocs.step.priceUnknown'));
        }
      }
      setJob(null); // the confirm is a HUMAN pause - a list ticking behind a modal reads as work
      if (!window.confirm(t('corpusDocs.confirmParseAll', { count: pending.length, credits: total, preset }))) {
        setBusy(false); return;
      }
      let started = 0;
      setJob({
        key: 'parse',
        items: pending.map((d) => ({ id: d.id, label: d.filename, status: 'idle', note: t('corpusDocs.step.queued') })),
      });
      for (const d of pending) {
        stepRow(d.id, 'active', t('corpusDocs.step.indexing'));
        try {
          await api.corpusAdmin('start_parse', { document_id: d.id, preset });
          started += 1;
          stepRow(d.id, 'done', t('corpusDocs.step.indexingStarted'));
        } catch (e) {
          toast.error(`${d.filename}: ${e.message}`);
          stepRow(d.id, 'error', e.message.slice(0, 60));
        }
      }
      toast.success(t('corpusDocs.parseAllStarted', { count: started }));
      loadDocs(dataset);
    } finally { setBusy(false); setJob(null); }
  };

  // N-87: status is pull-based by design (nothing polls RAGFlow); this makes the pull one click
  // for the whole dataset instead of one per row — the exact 17-clicks pain from the live build.
  const refreshAll = async () => {
    const active = (docs || []).filter((d) => ['parsing', 'pushed'].includes(d.status));
    if (active.length === 0) { toast.info(t('corpusDocs.refreshAllNone')); return; }
    setBusy(true);
    try {
      let settled = 0;
      setJob({
        key: 'refresh',
        items: active.map((d) => ({ id: d.id, label: d.filename, status: 'idle', note: t('corpusDocs.step.checking') })),
      });
      for (const d of active) {
        stepRow(d.id, 'active', t('corpusDocs.step.checking'));
        try {
          const r = await api.corpusAdmin('document_status', { document_id: d.id });
          if (r.billing?.settled) settled += 1;
          // The row reports the ENGINE's verdict, not "we asked successfully". `partial` is the
          // whole point of the coverage guard and has to be visible the moment it is known.
          const st = r.status || r.document?.status;
          if (st === 'indexed') stepRow(d.id, 'done', t('corpusDocs.step.indexed'));
          else if (st === 'partial') stepRow(d.id, 'warn', t('corpusDocs.step.partial'));
          else if (st === 'failed') stepRow(d.id, 'error', t('corpusDocs.step.failed'));
          else stepRow(d.id, 'pending', t('corpusDocs.step.stillIndexing'));
        } catch (e) {
          toast.error(`${d.filename}: ${e.message}`);
          stepRow(d.id, 'error', e.message.slice(0, 60));
        }
      }
      toast.success(t('corpusDocs.refreshAllDone', { settled, total: active.length }));
      loadDocs(dataset);
    } finally { setBusy(false); setJob(null); }
  };

  const parse = async (doc) => {
    setBusy(true);
    try {
      // Quote first, then confirm — the estimate is what makes this a decision, not a reflex.
      const q = await api.corpusAdmin('ingest_cost_preview', { document_id: doc.id, preset });
      const msg = q.will_charge
        ? t('corpusDocs.confirmParseCharged', { credits: q.estimated_credits, preset })
        : t('corpusDocs.confirmParseFree', { preset });
      if (!window.confirm(msg)) { setBusy(false); return; }
      const r = await api.corpusAdmin('start_parse', { document_id: doc.id, preset });
      toast.success(r.billing?.active
        ? t('corpusDocs.parseStartedCharged', { credits: r.billing.estimated_credits })
        : t('corpusDocs.parseStarted'));
      loadDocs(dataset);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const refreshStatus = async (doc) => {
    setBusy(true);
    try {
      const r = await api.corpusAdmin('document_status', { document_id: doc.id });
      if (r.billing?.settled) {
        toast.success(t('corpusDocs.settled', { credits: r.billing.charged_credits }));
      }
      loadDocs(dataset);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // N-65 — Ali: "saying a document or a law is obsolete and should not be used anymore".
  // Retirement is NOT deletion: the file stays, the archive copy stays, and it can be restored.
  // What changes is that retrieval stops returning it, so it can no longer ground an answer.
  const setRetired = async (doc, retire) => {
    if (retire && !window.confirm(t('corpusDocs.confirmRetire', { name: doc.filename }))) return;
    setBusy(true);
    try {
      const reason = retire ? (window.prompt(t('corpusDocs.retireReason')) || null) : null;
      await api.corpusAdmin(retire ? 'retire_document' : 'restore_document', {
        document_id: doc.id, reason,
      });
      toast.success(retire ? t('corpusDocs.retired') : t('corpusDocs.restored'));
      loadDocs(dataset);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async (doc) => {
    if (!window.confirm(t('corpusDocs.confirmDelete', { name: doc.filename }))) return;
    setBusy(true);
    try {
      await api.corpusAdmin('delete_document', { document_id: doc.id });
      toast.success(t('corpusDocs.deleted'));
      loadDocs(dataset);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const valid = (datasets || []).filter((d) => d.valid).map((d) => d.name);

  return (
    <SectionCard title={t('corpusDocs.title')} testId="corpus-documents-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="corpusDocs.hint" /></p>

      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <Field label={<T k="corpusDocs.dataset" />}>
          <Select value={dataset} onValueChange={setDataset}>
            <SelectTrigger data-testid="corpus-docs-dataset"><SelectValue placeholder={t('corpusDocs.pickDataset')} /></SelectTrigger>
            <SelectContent>
              {valid.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={<T k="corpusDocs.preset" />}>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger data-testid="corpus-docs-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label} · {p.cost_tier}×</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Upload */}
      <div className="mt-4 border-t border-border pt-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={<T k="corpusDocs.file" />}>
            <Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="corpus-docs-file" />
            {files.length > 1 && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="corpus-docs-filecount">
                {t('corpusDocs.filesSelected', { count: files.length })}
              </p>
            )}
          </Field>
          <Field label={<T k="corpusDocs.provenance" />}>
            <Select value={provenance} onValueChange={setProvenance}>
              <SelectTrigger data-testid="corpus-docs-provenance"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVENANCE.map((p) => <SelectItem key={p} value={p}>{t(`corpusDocs.prov_${p}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground mt-1"><T k="corpusDocs.provenanceHint" /></p>

        {quote && (
          <p className="text-sm mt-2" data-testid="corpus-docs-quote">
            {quote.will_charge
              ? t('corpusDocs.quoteCharged', { credits: quote.estimated_credits, tier: quote.cost_tier })
              : t('corpusDocs.quoteFree')}
            <span className="text-muted-foreground"> — {t('corpusDocs.quoteNote')}</span>
          </p>
        )}

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button onClick={upload} disabled={busy || !dataset || files.length === 0} data-testid="corpus-docs-upload">
            <Upload className="me-2 h-4 w-4" /> {t('corpusDocs.upload')}
          </Button>
          <Button variant="outline" onClick={parseAll} disabled={busy || !dataset} data-testid="corpus-docs-parse-all">
            {t('corpusDocs.parseAll')}
          </Button>
          <Button variant="outline" onClick={refreshAll} disabled={busy || !dataset} data-testid="corpus-docs-refresh-all">
            <RefreshCw className="me-2 h-3.5 w-3.5" /> {t('corpusDocs.refreshAll')}
          </Button>
          <span className="ms-2 text-xs text-muted-foreground"><T k="corpusDocs.uploadHint" /></span>
        </div>

        {/* N-90: the prototype builder's step list, one row per file. A 17-file batch used to
            read as a hung page; now every file says where it is and a failure keeps its own name. */}
        {job && (
          <div className="mt-3 flex flex-col gap-2">
            <p className="text-xs text-muted-foreground" data-testid="corpus-docs-progress-summary">
              {t(`corpusDocs.progress.${job.key}`, {
                done: job.items.filter((i) => i.status === 'done').length,
                total: job.items.length,
              })}
            </p>
            <StepProgressList items={job.items} testId="corpus-docs-progress" />
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="mt-4 border-t border-border pt-4 overflow-x-auto">
        {docs === null ? <Skeleton className="h-20 w-full" /> : (
          <table className="w-full text-sm" data-testid="corpus-docs-table">
            <thead>
              <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-start py-2 pe-4"><T k="corpusDocs.colFile" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusDocs.colStatus" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusDocs.colProv" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusDocs.colTokens" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusDocs.colChunks" /></th>
                <th className="text-start py-2"><T k="corpusDocs.colActions" /></th>
              </tr>
            </thead>
            <tbody className="font-data">
              {docs.map((d) => (
                <tr key={d.id} className={`border-b border-border ${d.retired ? 'opacity-60' : ''}`} data-testid={`corpus-doc-${d.id}`}>
                  <td className="py-2 pe-4 text-foreground flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className={d.retired ? 'line-through' : ''}>{d.filename}</span>
                    {/* N-65: a retired document is still HERE — it just cannot ground an answer.
                        Showing it struck through rather than hiding it is the point: curation you
                        cannot see is indistinguishable from a corpus that lost a file. */}
                    {d.retired && (
                      <span className="text-xs text-warning" data-testid={`corpus-doc-retired-${d.id}`}>
                        {t('corpusDocs.retiredBadge')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pe-4"><StatusPill status={d.status} detail={d.status_detail} /></td>
                  <td className="py-2 pe-4 text-muted-foreground">{d.provenance}</td>
                  <td className="py-2 pe-4">{d.token_count ?? '—'}</td>
                  <td className="py-2 pe-4">{d.chunk_count ?? '—'}</td>
                  <td className="py-2 flex gap-2">
                    {d.status !== 'indexed' && (
                      <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => parse(d)} data-testid={`corpus-doc-parse-${d.id}`}>
                        {t('corpusDocs.parse')}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => refreshStatus(d)} data-testid={`corpus-doc-refresh-${d.id}`}>
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => setRetired(d, !d.retired)} data-testid={`corpus-doc-retire-${d.id}`}>
                      {d.retired
                        ? <><RotateCcw className="h-3.5 w-3.5 me-1" /><T k="corpusDocs.restore" /></>
                        : <><Archive className="h-3.5 w-3.5 me-1" /><T k="corpusDocs.retire" /></>}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => remove(d)} data-testid={`corpus-doc-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr><td colSpan={6} className="py-3 text-muted-foreground" data-testid="corpus-docs-empty">
                  {dataset ? t('corpusDocs.none') : t('corpusDocs.pickDataset')}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </SectionCard>
  );
}
